import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getScrollManager } from '../hooks/useScrollRestoration';

interface ScrollRestorationProps {
  /**
   * Whether to enable automatic scroll restoration globally
   */
  enabled?: boolean;
  /**
   * Delay before restoring scroll position (ms)
   */
  restoreDelay?: number;
  /**
   * Paths that should always scroll to top instead of restoring position
   */
  forceTopPaths?: string[];
  /**
   * Function to determine if scroll should be restored based on location
   */
  shouldRestore?: (location: Location) => boolean;
}

/**
 * Global scroll restoration component that should be placed once in your app layout.
 * This handles automatic scroll restoration across all routes.
 */
export const ScrollRestoration: React.FC<ScrollRestorationProps> = ({
  enabled = true,
  restoreDelay = 50,
  forceTopPaths = [],
  shouldRestore
}) => {
  const location = useLocation();
  const scrollManager = getScrollManager();

  // Create a memoized location object to avoid creating new objects on each render
  const memoizedLocation = useMemo(() => ({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    state: location.state,
    key: location.key
  }), [location.pathname, location.search, location.hash, location.state, location.key]);

  useEffect(() => {
    if (!enabled) return;

    const currentPath = memoizedLocation.pathname + memoizedLocation.search;
    const shouldForceTop = forceTopPaths.includes(memoizedLocation.pathname);
    
    const customShouldRestore = shouldRestore ? shouldRestore(memoizedLocation as any) : true;

    if (shouldForceTop || !customShouldRestore) {
      // Scroll to top immediately
      setTimeout(() => {
        window.scrollTo({
          left: 0,
          top: 0,
          behavior: 'auto'
        });
      }, 0);
      return;
    }

    // Restore scroll position with delay
    const timer = setTimeout(() => {
      const position = scrollManager.getPosition(currentPath);
      if (position) {
        requestAnimationFrame(() => {
          window.scrollTo({
            left: position.x,
            top: position.y,
            behavior: 'auto'
          });
        });
      }
    }, restoreDelay);

    return () => clearTimeout(timer);
  }, [memoizedLocation, enabled, restoreDelay, forceTopPaths, shouldRestore, scrollManager]);

  // Save scroll position periodically
  useEffect(() => {
    if (!enabled) return;

    let throttleTimer: NodeJS.Timeout;
    
    const handleScroll = () => {
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
      throttleTimer = setTimeout(() => {
        const currentPath = location.pathname + location.search;
        scrollManager.savePosition(currentPath);
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [enabled, location.pathname, location.search, scrollManager]);

  return null; // This component doesn't render anything
};

export default ScrollRestoration; 